import {
  appStorageKeyFromUrl,
  isPubliclyServableStorageKey,
} from "./storageContract";

export type EventVideoSource =
  | { type: "youtube"; playbackUrl: string }
  | { type: "vimeo"; playbackUrl: string }
  | { type: "storage"; playbackUrl: string; storageKey: string };

const YOUTUBE_ID = /^[A-Za-z0-9_-]{11}$/;
const VIMEO_ID = /^\d{6,12}$/;
const PUBLIC_VIDEO_EXTENSIONS = new Set(["mp4", "webm", "ogv", "ogg", "m4v"]);

export function isSupportedEventVideoStorageKey(key: string): boolean {
  if (!isPubliclyServableStorageKey(key)) return false;
  const extension = key.split(".").pop()?.toLowerCase() ?? "";
  return PUBLIC_VIDEO_EXTENSIONS.has(extension);
}

export function resolveEventVideoSource(value: string | null | undefined): EventVideoSource | null {
  const raw = value?.trim();
  if (!raw) return null;

  const storageKey = appStorageKeyFromUrl(raw);
  if (storageKey && isSupportedEventVideoStorageKey(storageKey)) {
    return { type: "storage", playbackUrl: raw, storageKey };
  }

  let parsed: URL;
  try {
    parsed = new URL(raw);
  } catch {
    return null;
  }
  if (parsed.protocol !== "https:") return null;

  const host = parsed.hostname.toLowerCase().replace(/^www\./, "");
  let youtubeId: string | null = null;
  if (host === "youtu.be") {
    youtubeId = parsed.pathname.split("/").filter(Boolean)[0] ?? null;
  } else if (host === "youtube.com" || host === "m.youtube.com") {
    if (parsed.pathname === "/watch") youtubeId = parsed.searchParams.get("v");
    else youtubeId = parsed.pathname.match(/^\/(?:embed|shorts)\/([^/?#]+)/)?.[1] ?? null;
  } else if (host === "youtube-nocookie.com") {
    youtubeId = parsed.pathname.match(/^\/embed\/([^/?#]+)/)?.[1] ?? null;
  }
  if (youtubeId && YOUTUBE_ID.test(youtubeId)) {
    return {
      type: "youtube",
      playbackUrl: `https://www.youtube-nocookie.com/embed/${youtubeId}`,
    };
  }

  let vimeoId: string | null = null;
  if (host === "vimeo.com") {
    vimeoId = parsed.pathname.split("/").filter(Boolean)[0] ?? null;
  } else if (host === "player.vimeo.com") {
    vimeoId = parsed.pathname.match(/^\/video\/(\d+)/)?.[1] ?? null;
  }
  if (vimeoId && VIMEO_ID.test(vimeoId)) {
    return {
      type: "vimeo",
      playbackUrl: `https://player.vimeo.com/video/${vimeoId}`,
    };
  }

  return null;
}